const DRAFT_KEY_PREFIX = "boho_event_application_";
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const APPLICATION_INCOMPLETE_QUERY = "application_incomplete";

export const APPLICATION_INCOMPLETE_MESSAGE =
  "Your application details could not be loaded. Please complete your selection and try again.";

const getDraftKey = (eventId) => `${DRAFT_KEY_PREFIX}${eventId}`;

export const normalizeQueryArray = (value) => {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
};

export const saveEventApplicationDraft = (eventId, fields) => {
  if (typeof window === "undefined" || !eventId) return;

  try {
    sessionStorage.setItem(
      getDraftKey(eventId),
      JSON.stringify({
        ...fields,
        savedAt: Date.now(),
      }),
    );
  } catch (err) {
    console.warn("Could not save application draft", err);
  }
};

export const loadEventApplicationDraft = (eventId) => {
  if (typeof window === "undefined" || !eventId) return null;

  try {
    const raw = sessionStorage.getItem(getDraftKey(eventId));
    if (!raw) return null;

    const draft = JSON.parse(raw);
    if (draft.savedAt && Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
      clearEventApplicationDraft(eventId);
      return null;
    }

    return draft;
  } catch (err) {
    console.warn("Could not load application draft", err);
    return null;
  }
};

export const clearEventApplicationDraft = (eventId) => {
  if (typeof window === "undefined" || !eventId) return;

  try {
    sessionStorage.removeItem(getDraftKey(eventId));
  } catch (err) {
    console.warn("Could not clear application draft", err);
  }
};

/** Query params override draft when both are present. */
export const resolveApplicationConfirm = (query = {}, eventId) => {
  const draft = loadEventApplicationDraft(eventId) || {};
  const productIdsFromQuery = normalizeQueryArray(query.product_id);

  return {
    ...draft,
    ...query,
    booth_id: query.booth_id ?? draft.booth_id,
    booth_size: query.booth_size ?? draft.booth_size,
    booth_price: query.booth_price ?? draft.booth_price,
    notes: query.notes ?? draft.notes,
    ada: query.ada ?? draft.ada,
    electricity: query.electricity ?? draft.electricity,
    product_id:
      productIdsFromQuery.length > 0
        ? productIdsFromQuery
        : normalizeQueryArray(draft.product_id ?? draft.product_ids),
  };
};

export const isApplicationConfirmValid = (confirm) =>
  Boolean(confirm?.booth_id);

export const getSafeReturnUrl = (returnUrl) => {
  if (typeof returnUrl !== "string" || !returnUrl.startsWith("/")) {
    return "/dashboard";
  }
  if (returnUrl.startsWith("//")) {
    return "/dashboard";
  }
  return returnUrl;
};

export const resolveChosenBooth = (event, appConfirm) => {
  if (!appConfirm?.booth_id) return null;

  if (appConfirm.booth_id === "CDCD") {
    return {
      id: "CDCD",
      title: "food truck",
      booth_size_id: "CDCD",
      booth_price: event.event_price_food_truck,
      booth_size: "Food Truck",
    };
  }

  if (appConfirm.booth_id === "ABAB") {
    return {
      id: "ABAB",
      title: "food space",
      booth_size_id: "ABAB",
      booth_price: event.event_price_food_space,
      booth_size: "Food Space",
    };
  }

  return {
    booth_size_id: appConfirm.booth_id,
    booth_price: appConfirm.booth_price,
    booth_size: appConfirm.booth_size,
  };
};

export const resolveApplicationProducts = (event, appConfirm) => {
  const productInfo = event?.event_product_info || [];
  const ids = normalizeQueryArray(appConfirm?.product_id).map((id) =>
    parseInt(id, 10),
  );

  return productInfo.filter((product) => ids.includes(product.id));
};

export const computeApplicationTotal = (booth, products) => {
  const boothPrice = parseInt(booth?.booth_price, 10) || 0;
  const productsTotal = (products || []).reduce(
    (sum, product) => sum + (parseInt(product?.product_price, 10) || 0),
    0,
  );

  return boothPrice + productsTotal;
};
